const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const mix = (start, end, amount) => start + ((end - start) * amount);

// Smoothstep makes the sunrise begin and finish gently instead of changing
// abruptly at either end of the routine.
const smoothstep = (value) => value * value * (3 - (2 * value));

const normaliseOrbNumber = (orbNumber) => {
  const safeOrbNumber = Number.isFinite(orbNumber) ? Math.round(orbNumber) : 1;
  return clamp(safeOrbNumber, 1, 2);
};

const roundedMix = (start, end, amount) => Math.round(mix(start, end, amount));
const alphaMix = (start, end, amount) => Number(mix(start, end, amount).toFixed(2));
const rgbMix = (start, end, amount) => (
  `rgb(${start.map((channel, index) => roundedMix(channel, end[index], amount)).join(', ')})`
);

export const getSunriseOrbVisual = (progress = 0, orbNumber = 1) => {
  const safeProgress = Number.isFinite(progress) ? progress : 0;
  // The right orb is a touch further into the sunrise, keeping the pair warm
  // and varied while both still travel from deep red to daylight yellow.
  const phaseOffset = orbNumber === 2 ? 0.06 : 0;
  const eased = smoothstep(clamp(safeProgress + phaseOffset, 0, 1));
  // Travel forward through the red/orange/yellow arc. Starting just below
  // 360 keeps the opening clearly crimson rather than copper-brown.
  const hue = Math.round(mix(356, 412, eased)) % 360;
  const saturation = Math.round(mix(90, 96, eased));
  const lightness = Math.round(mix(13, 58, eased));
  const shadowLightness = Math.round(mix(18, 62, eased));

  return {
    backgroundColor: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
    // A neutral highlight preserves TapTap's luminous orb style without adding
    // the yellow cast that made the opening red appear copper-coloured.
    backgroundImage: 'radial-gradient(circle at 45% 40%, rgba(255, 235, 240, 0.18) 0%, rgba(255, 255, 255, 0.04) 48%, rgba(32, 0, 10, 0.24) 100%)',
    shadow: `hsla(${hue}, ${saturation}%, ${shadowLightness}%, 0.72)`,
  };
};

// A clean sunset journey runs in the opposite direction to Wake Up: gold and
// orange settle through coral/violet twilight into near-dark navy and plum.
// Explicit stages avoid the muddy brown that can result from mixing a bright
// yellow directly with a night colour, while the familiar dark core, coloured
// body and luminous rim keep the pair recognisably TapTap.
export const getSleepDuskOrbVisual = (progress = 0, orbNumber = 1) => {
  const safeProgress = Number.isFinite(progress) ? progress : 0;
  const eased = smoothstep(clamp(safeProgress, 0, 1));
  const safeOrbNumber = normaliseOrbNumber(orbNumber);
  const palettes = {
    1: {
      stages: [
        { core: [161, 98, 7], body: [250, 204, 21], rim: [254, 240, 138], shadow: [250, 204, 21], shadowAlpha: 0.48 },
        { core: [124, 45, 18], body: [249, 115, 22], rim: [253, 186, 116], shadow: [249, 115, 22], shadowAlpha: 0.3 },
        { core: [88, 20, 66], body: [153, 27, 90], rim: [219, 75, 135], shadow: [153, 27, 90], shadowAlpha: 0.16 },
        { core: [2, 6, 23], body: [7, 15, 35], rim: [23, 37, 84], shadow: [23, 37, 84], shadowAlpha: 0.05 },
      ],
    },
    2: {
      stages: [
        { core: [154, 52, 18], body: [251, 146, 60], rim: [254, 215, 170], shadow: [251, 146, 60], shadowAlpha: 0.46 },
        { core: [127, 29, 29], body: [220, 38, 38], rim: [248, 113, 113], shadow: [220, 38, 38], shadowAlpha: 0.28 },
        { core: [76, 29, 149], body: [109, 40, 160], rim: [167, 100, 210], shadow: [109, 40, 160], shadowAlpha: 0.15 },
        { core: [8, 2, 15], body: [20, 7, 36], rim: [55, 22, 78], shadow: [55, 22, 78], shadowAlpha: 0.05 },
      ],
    },
  };
  const palette = palettes[safeOrbNumber];
  const segmentCount = palette.stages.length - 1;
  const scaledProgress = eased * segmentCount;
  const segmentIndex = Math.min(Math.floor(scaledProgress), segmentCount - 1);
  const phaseProgress = scaledProgress - segmentIndex;
  const from = palette.stages[segmentIndex];
  const to = palette.stages[segmentIndex + 1];
  const core = rgbMix(from.core, to.core, phaseProgress);
  const body = rgbMix(from.body, to.body, phaseProgress);
  const rim = rgbMix(from.rim, to.rim, phaseProgress);
  const shadow = from.shadow.map(
    (channel, index) => roundedMix(channel, to.shadow[index], phaseProgress)
  );
  const shadowAlpha = alphaMix(from.shadowAlpha, to.shadowAlpha, phaseProgress);

  return {
    backgroundColor: body,
    gradient: `radial-gradient(circle, ${core} 0%, ${body} 70%, ${rim} 100%)`,
    shadow: `rgba(${shadow.join(', ')}, ${shadowAlpha})`,
  };
};

// Refocus uses TapTap's two brand colour families as a stable, high-contrast
// pair rather than cycling through unrelated random colours.
export const getFocusOrbVisual = (orbNumber = 1) => {
  const safeOrbNumber = normaliseOrbNumber(orbNumber);

  if (safeOrbNumber === 2) {
    return {
      backgroundColor: '#8b5cf6',
      backgroundImage: 'radial-gradient(circle at 45% 40%, rgba(244, 232, 255, 0.72) 0%, rgba(168, 85, 247, 0.52) 44%, rgba(88, 28, 135, 0.36) 100%)',
      shadow: 'rgba(168, 85, 247, 0.86)',
    };
  }

  return {
    backgroundColor: '#0ea5e9',
    backgroundImage: 'radial-gradient(circle at 45% 40%, rgba(224, 247, 255, 0.72) 0%, rgba(59, 130, 246, 0.5) 44%, rgba(30, 64, 175, 0.34) 100%)',
    shadow: 'rgba(14, 165, 233, 0.86)',
  };
};
